import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { TRButton } from "@/components/TRButton";
import { getCategory, boxImage } from "@/lib/catalog";

export default function Collection() {
    const { category, gender } = useParams();
    const navigate = useNavigate();
    const c = getCategory(category);
    const g = c.genders.includes(gender) ? gender : "men";
    const title = `${g === "men" ? "Men's" : "Women's"} ${c.label}`;

    const go = (params) => {
        const q = new URLSearchParams(params).toString();
        navigate(`/configure/${c.id}/${g}?${q}`);
    };

    const single = c.styles.length <= 1;
    const gridCols = single
        ? "max-w-md mx-auto grid-cols-1"
        : c.styles.length === 2
          ? "sm:grid-cols-2 max-w-3xl mx-auto"
          : c.styles.length === 4
            ? "sm:grid-cols-2 lg:grid-cols-4"
            : "md:grid-cols-3";

    return (
        <div data-testid={`collection-${c.id}-${g}`} className="pt-28 md:pt-36">
            <section className="max-w-[1500px] mx-auto px-5 sm:px-8">
                <Reveal>
                    <span className="eyebrow text-ink/50">
                        {c.label} · {g === "men" ? "Men" : "Women"}
                    </span>
                    <h1 className="mt-5 font-serif font-light text-5xl md:text-7xl tracking-[-0.04em]">
                        {title}
                    </h1>
                    <p className="mt-6 max-w-md text-base text-ink/65 leading-relaxed">
                        {c.isBundle
                            ? "5 boxers · 5 undershirts · 10 pairs of socks. One box — your whole drawer, renewed."
                            : `Choose your color — black or white. Receive ${c.unitsPerBox} premium ${c.pieceNoun}.`}
                    </p>
                </Reveal>
            </section>

            {c.heroImage && (
                <section className="max-w-[1500px] mx-auto px-5 sm:px-8 mt-12 md:mt-16">
                    <Reveal>
                        <div className="grid grid-cols-2 gap-3 md:gap-4">
                            <button
                                type="button"
                                onClick={() => go({ box: "one", color: "black" })}
                                data-testid="hero-tile-black"
                                className="group relative overflow-hidden border border-line hover:border-ink aspect-[3/4] bg-secondary transition-colors duration-300"
                            >
                                <img loading="lazy" decoding="async"
                                    src={(c.heroImage[g] || c.heroImage).black}
                                    alt={`${c.label} lifestyle — black`}
                                    className="absolute inset-0 h-full w-full object-contain transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05] group-active:scale-[1.04]"
                                />
                                <span className="absolute left-3 bottom-3 eyebrow text-ink/70">Black</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => go({ box: "one", color: "white" })}
                                data-testid="hero-tile-white"
                                className="group relative overflow-hidden border border-line hover:border-ink aspect-[3/4] bg-secondary transition-colors duration-300"
                            >
                                <img loading="lazy" decoding="async"
                                    src={(c.heroImage[g] || c.heroImage).white}
                                    alt={`${c.label} lifestyle — white`}
                                    className="absolute inset-0 h-full w-full object-contain transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05] group-active:scale-[1.04]"
                                />
                                <span className="absolute left-3 bottom-3 eyebrow text-ink/70">White</span>
                            </button>
                        </div>
                    </Reveal>
                </section>
            )}

            {/* Models */}
            <section className="max-w-[1500px] mx-auto px-5 sm:px-8 mt-16 md:mt-24">
                <div className={`grid gap-8 md:gap-6 ${gridCols}`}>
                    {c.styles.map((s, i) => (
                        <Reveal key={s.id} delay={i * 0.08}>
                            <div
                                className="group flex flex-col h-full"
                                data-testid={`model-card-${s.id}`}
                            >
                                <div className="flex items-baseline justify-between mb-3">
                                    <h3 className="font-serif text-2xl md:text-3xl tracking-[-0.03em]">
                                        {s.name}
                                    </h3>
                                    <span className="font-serif text-lg">€89</span>
                                </div>
                                <p className="eyebrow text-ink/40 mb-4 min-h-[2.4rem]">
                                    {c.styleCopy[g][s.id]}
                                </p>

                                <div className="grid grid-cols-2 gap-3">
                                    {["black", "white"].map((col) => (
                                        <button
                                            key={col}
                                            onClick={() => go({ style: s.id, box: "one", color: col })}
                                            data-testid={`select-${s.id}-${col}`}
                                            className="group/tile relative overflow-hidden border border-line hover:border-ink aspect-[3/4] bg-secondary transition-colors duration-300"
                                        >
                                            {c.hasModelPhotos ? (
                                                <img loading="lazy" decoding="async"
                                                    src={c.modelImages[g][s.id][col]}
                                                    alt={`${s.name} ${g} ${col}`}
                                                    className="absolute inset-0 h-full w-full object-contain transition-transform duration-700 group-hover/tile:scale-[1.03]"
                                                />
                                            ) : (
                                                <img loading="lazy" decoding="async"
                                                    src={boxImage(c.id, "one", col, g)}
                                                    alt={`${s.name} ${col}`}
                                                    className="absolute inset-0 h-full w-full object-contain transition-transform duration-700 group-hover/tile:scale-[1.03]"
                                                />
                                            )}
                                            <span className="absolute inset-x-0 bottom-0 py-2 text-center text-[0.6rem] uppercase tracking-[0.22em] bg-paper/85 text-ink">
                                                {col}
                                            </span>
                                            <span className="absolute inset-0 flex items-center justify-center bg-ink/0 group-hover/tile:bg-ink/10 transition-colors duration-300">
                                                <span className="opacity-0 group-hover/tile:opacity-100 transition-opacity duration-300 bg-ink text-paper px-4 py-2 text-[0.6rem] uppercase tracking-[0.18em]">
                                                    Select
                                                </span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                <p className="mt-4 text-[0.62rem] uppercase tracking-[0.2em] text-ink/45">
                                    Choose black or white
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* Assortment / Starter promo */}
            {c.boxTypes.length > 1 && (
            <section className="max-w-[1500px] mx-auto px-5 sm:px-8 mt-24 md:mt-36 mb-24 md:mb-36">
                <Reveal>
                    <div className="grid md:grid-cols-2 border border-line">
                        <button
                            type="button"
                            onClick={() => go({ box: "starter" })}
                            data-testid="starter-promo-tile"
                            className="group relative aspect-[4/3] md:aspect-auto bg-secondary overflow-hidden text-left"
                        >
                            <img loading="lazy" decoding="async"
                                src={boxImage(c.id, "starter", "black")}
                                alt={c.boxTypes[1].name}
                                className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05] group-active:scale-[1.04]"
                            />
                        </button>
                        <div className="p-10 md:p-16 flex flex-col justify-center">
                            <span className="eyebrow text-ink/50">Not sure yet?</span>
                            <h2 className="mt-5 font-serif text-4xl md:text-5xl tracking-[-0.03em]">
                                {c.boxTypes[1].name}
                            </h2>
                            <p className="mt-5 text-ink/65 leading-relaxed max-w-sm">
                                {c.boxTypes[1].short} The easiest way to find the fit
                                you'll live in.
                            </p>
                            <div className="mt-8 flex items-center gap-6">
                                <span className="font-serif text-2xl">€89</span>
                                <TRButton
                                    onClick={() => go({ box: "starter" })}
                                    data-testid="select-starter"
                                >
                                    Build {c.boxTypes[1].name}
                                    <ArrowRight size={15} strokeWidth={1.5} />
                                </TRButton>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </section>
            )}
        </div>
    );
}
